// src/hospital/controllers/hospitalController.js
// All API calls for every Hospital Management module
// ─────────────────────────────────────────────────────────────────────────────
import apiService from '../services/apiService';
import {
  Patient, Doctor, Nurse, Appointment, Department, Ward, Bed,
  OPDVisit, IPDAdmission, Emergency, LabTest, PathologyTest,
  RadiologyTest, Medicine, BloodUnit, Surgery, Ambulance,
  ICUAdmission, MaternityCase, HospitalBill, InsuranceClaim,
  MedicalRecord, Prescription, VitalSign, DietPlan, MortalityRecord,
} from '../models/Hospital';

// ─────────────────────────────────────────────────────────────────────────────
// HELPER
// ─────────────────────────────────────────────────────────────────────────────
const unwrap = (res) => {
  if (!res) return {};
  if ('success' in res) return res;
  if (res.data && 'success' in res.data) return res.data;
  return res;
};

const buildQP = (filters = {}) => {
  const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined));
  return new URLSearchParams(clean).toString();
};

// Generic CRUD factory — used by all modules
const makeCRUD = (endpoint, ModelClass) => ({
  getAll: async (filters = {}) => {
    try {
      const qp  = buildQP(filters);
      const raw = unwrap(await apiService.get(qp ? `${endpoint}?${qp}` : endpoint));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => ModelClass.fromJSON(d)), total: raw.total || list.length, page: raw.page || 1, totalPages: raw.totalPages || 1 };
    } catch (err) {
      return { success: false, data: [], total: 0, message: err?.response?.data?.message || err.message };
    }
  },

  getById: async (id) => {
    try {
      const raw = unwrap(await apiService.get(`${endpoint}/${id}`));
      return { success: true, data: ModelClass.fromJSON(raw.data || raw) };
    } catch (err) {
      return { success: false, data: null, message: err?.response?.data?.message || err.message };
    }
  },

  create: async (data) => {
    try {
      const obj        = new ModelClass(data);
      const validation = obj.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      const raw = unwrap(await apiService.post(endpoint, obj.toJSON()));
      if (raw.success === false) return { success: false, errors: raw.errors || [], message: raw.message };
      return { success: true, data: ModelClass.fromJSON(raw.data || raw), message: raw.message || 'Created successfully' };
    } catch (err) {
      const e = err?.response?.data;
      return { success: false, message: e?.message || err.message, errors: e?.errors || [] };
    }
  },

  update: async (id, data) => {
    try {
      const obj        = new ModelClass({ ...data, id });
      const validation = obj.validate();
      if (!validation.isValid) return { success: false, errors: validation.errors };
      const raw = unwrap(await apiService.put(`${endpoint}/${id}`, obj.toJSON()));
      if (raw.success === false) return { success: false, errors: raw.errors || [], message: raw.message };
      return { success: true, data: ModelClass.fromJSON(raw.data || raw), message: raw.message || 'Updated successfully' };
    } catch (err) {
      const e = err?.response?.data;
      return { success: false, message: e?.message || err.message, errors: e?.errors || [] };
    }
  },

  delete: async (id) => {
    try {
      const raw = unwrap(await apiService.delete(`${endpoint}/${id}`));
      return { success: true, message: raw.message || 'Deleted successfully' };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  getStats: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`${endpoint}/stats${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: raw.data || raw };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// 1. PATIENT REGISTRATION
// ─────────────────────────────────────────────────────────────────────────────
export const patientController = {
  ...makeCRUD('hospital/patients', Patient),

  // Search patients (for autocomplete in forms)
  search: async (query) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/patients/search?q=${encodeURIComponent(query)}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Patient.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  // Get patient medical timeline
  getTimeline: async (patientId) => {
    try {
      const raw = unwrap(await apiService.get(`hospital/patients/${patientId}/timeline`));
      return { success: true, data: raw.data || [] };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  // Upload patient photo
  uploadPhoto: async (patientId, file) => {
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const raw = unwrap(await apiService.post(`hospital/patients/${patientId}/photo`, fd));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. PATIENT RECORDS
// ─────────────────────────────────────────────────────────────────────────────
export const medicalRecordController = {
  ...makeCRUD('hospital/medical-records', MedicalRecord),

  getByPatient: async (patientId, filters = {}) => {
    try {
      const qp  = buildQP({ ...filters, patientId });
      const raw = unwrap(await apiService.get(`hospital/medical-records?${qp}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => MedicalRecord.fromJSON(d)), total: raw.total || list.length };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  uploadAttachment: async (recordId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const raw = unwrap(await apiService.post(`hospital/medical-records/${recordId}/attachments`, fd));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. APPOINTMENTS
// ─────────────────────────────────────────────────────────────────────────────
export const appointmentController = {
  ...makeCRUD('hospital/appointments', Appointment),

  getByDate: async (date, filters = {}) => {
    try {
      const qp  = buildQP({ ...filters, date });
      const raw = unwrap(await apiService.get(`hospital/appointments?${qp}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Appointment.fromJSON(d)), total: raw.total || list.length };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  updateStatus: async (id, status, notes = '') => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/appointments/${id}/status`, { status, notes }));
      return { success: true, data: Appointment.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  getDoctorSlots: async (doctorId, date) => {
    try {
      const raw = unwrap(await apiService.get(`hospital/appointments/slots?doctorId=${doctorId}&date=${date}`));
      return { success: true, data: raw.data || [] };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  reschedule: async (id, { appointmentDate, appointmentTime }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/appointments/${id}/reschedule`, { appointmentDate, appointmentTime }));
      return { success: true, data: Appointment.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. DOCTORS
// ─────────────────────────────────────────────────────────────────────────────
export const doctorController = {
  ...makeCRUD('hospital/doctors', Doctor),

  search: async (query) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/doctors/search?q=${encodeURIComponent(query)}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Doctor.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  getByDepartment: async (departmentId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/doctors?departmentId=${departmentId}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Doctor.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  getSchedule: async (doctorId, startDate, endDate) => {
    try {
      const raw = unwrap(await apiService.get(`hospital/doctors/${doctorId}/schedule?startDate=${startDate}&endDate=${endDate}`));
      return { success: true, data: raw.data || [] };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  updateAvailability: async (doctorId, availability) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/doctors/${doctorId}/availability`, availability));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. NURSES
// ─────────────────────────────────────────────────────────────────────────────
export const nurseController = {
  ...makeCRUD('hospital/nurses', Nurse),

  getByWard: async (wardId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/nurses?wardId=${wardId}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Nurse.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  assignWard: async (nurseId, wardId) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/nurses/${nurseId}/assign-ward`, { wardId }));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. DEPARTMENTS
// ─────────────────────────────────────────────────────────────────────────────
export const departmentController = {
  ...makeCRUD('hospital/departments', Department),

  getAllList: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/departments/list${companyId ? `?companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Department.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 7. WARDS & ROOMS
// ─────────────────────────────────────────────────────────────────────────────
export const wardController = {
  ...makeCRUD('hospital/wards', Ward),

  getAvailable: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/wards/available${companyId ? `?companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Ward.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 8. BED MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
export const bedController = {
  ...makeCRUD('hospital/beds', Bed),

  getByWard: async (wardId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/beds?wardId=${wardId}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Bed.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  assignPatient: async (bedId, { patientId, patientName, admissionDate }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/beds/${bedId}/assign`, { patientId, patientName, admissionDate }));
      return { success: true, data: Bed.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  releasePatient: async (bedId) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/beds/${bedId}/release`, {}));
      return { success: true, data: Bed.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  getBedMatrix: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`hospital/beds/matrix${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: raw.data || [] };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 9. OPD MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
export const opdController = {
  ...makeCRUD('hospital/opd', OPDVisit),

  getTodayQueue: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/opd/today${companyId ? `?companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => OPDVisit.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  updateStatus: async (id, status) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/opd/${id}/status`, { status }));
      return { success: true, data: OPDVisit.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  addVitals: async (visitId, vitals) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/opd/${visitId}/vitals`, vitals));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  addPrescription: async (visitId, prescription) => {
    try {
      const raw = unwrap(await apiService.post(`hospital/opd/${visitId}/prescription`, prescription));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 10. IPD MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
export const ipdController = {
  ...makeCRUD('hospital/ipd', IPDAdmission),

  discharge: async (id, dischargeData) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/ipd/${id}/discharge`, dischargeData));
      return { success: true, data: IPDAdmission.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  transferWard: async (id, { wardId, bedId }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/ipd/${id}/transfer`, { wardId, bedId }));
      return { success: true, data: IPDAdmission.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  addCharge: async (id, charge) => {
    try {
      const raw = unwrap(await apiService.post(`hospital/ipd/${id}/charges`, charge));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 11. EMERGENCY
// ─────────────────────────────────────────────────────────────────────────────
export const emergencyController = {
  ...makeCRUD('hospital/emergency', Emergency),

  getActive: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/emergency/active${companyId ? `?companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Emergency.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  updateStatus: async (id, status, notes = '') => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/emergency/${id}/status`, { status, notes }));
      return { success: true, data: Emergency.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  admitToIPD: async (emergencyId, ipdData) => {
    try {
      const raw = unwrap(await apiService.post(`hospital/emergency/${emergencyId}/admit`, ipdData));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 12. LABORATORY
// ─────────────────────────────────────────────────────────────────────────────
export const labController = {
  ...makeCRUD('hospital/laboratory', LabTest),

  updateResult: async (id, { result, normalRange, unit, status, remarks }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/laboratory/${id}/result`, { result, normalRange, unit, status, remarks }));
      return { success: true, data: LabTest.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  collectSample: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/laboratory/${id}/collect`, { sampleCollectedAt: new Date().toISOString() }));
      return { success: true, data: LabTest.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  getPendingTests: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/laboratory?status=Pending${companyId ? `&companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => LabTest.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 13. PATHOLOGY
// ─────────────────────────────────────────────────────────────────────────────
export const pathologyController = {
  ...makeCRUD('hospital/pathology', PathologyTest),

  updateReport: async (id, { findings, impression, status }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/pathology/${id}/report`, { findings, impression, status }));
      return { success: true, data: PathologyTest.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 14. RADIOLOGY
// ─────────────────────────────────────────────────────────────────────────────
export const radiologyController = {
  ...makeCRUD('hospital/radiology', RadiologyTest),

  updateReport: async (id, { findings, impression, status }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/radiology/${id}/report`, { findings, impression, status }));
      return { success: true, data: RadiologyTest.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  uploadImage: async (id, file) => {
    try {
      const fd = new FormData();
      fd.append('image', file);
      const raw = unwrap(await apiService.post(`hospital/radiology/${id}/image`, fd));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 15. PHARMACY
// ─────────────────────────────────────────────────────────────────────────────
export const pharmacyController = {
  ...makeCRUD('hospital/pharmacy', Medicine),

  getLowStock: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/pharmacy/low-stock${companyId ? `?companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Medicine.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  getExpired: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/pharmacy/expired${companyId ? `?companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Medicine.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  updateStock: async (id, { quantity, type, reason }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/pharmacy/${id}/stock`, { quantity, type, reason }));
      return { success: true, data: Medicine.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  dispense: async (id, dispensedData) => {
    try {
      const raw = unwrap(await apiService.post(`hospital/pharmacy/${id}/dispense`, dispensedData));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  search: async (query) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/pharmacy/search?q=${encodeURIComponent(query)}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Medicine.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 16. BLOOD BANK
// ─────────────────────────────────────────────────────────────────────────────
export const bloodBankController = {
  ...makeCRUD('hospital/blood-bank', BloodUnit),

  getByBloodGroup: async (bloodGroup, companyId) => {
    try {
      const qp   = buildQP({ bloodGroup, companyId, status: 'Available' });
      const raw  = unwrap(await apiService.get(`hospital/blood-bank?${qp}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => BloodUnit.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  issue: async (unitId, { patientId, patientName, issuedBy }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/blood-bank/${unitId}/issue`, { patientId, patientName, issuedBy, issuedAt: new Date().toISOString() }));
      return { success: true, data: BloodUnit.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  getInventorySummary: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`hospital/blood-bank/summary${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: raw.data };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 17. OPERATION THEATER
// ─────────────────────────────────────────────────────────────────────────────
export const surgeryController = {
  ...makeCRUD('hospital/operation-theater', Surgery),

  updateStatus: async (id, status, notes = '') => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/operation-theater/${id}/status`, { status, notes }));
      return { success: true, data: Surgery.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  getTodaySchedule: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/operation-theater/today${companyId ? `?companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Surgery.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 18. AMBULANCE SERVICE
// ─────────────────────────────────────────────────────────────────────────────
export const ambulanceController = {
  ...makeCRUD('hospital/ambulance', Ambulance),

  getAvailable: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/ambulance/available${companyId ? `?companyId=${companyId}` : ''}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Ambulance.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  dispatch: async (id, dispatchData) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/ambulance/${id}/dispatch`, dispatchData));
      return { success: true, data: Ambulance.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  returnToBase: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/ambulance/${id}/return`, {}));
      return { success: true, data: Ambulance.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 19. ICU MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
export const icuController = {
  ...makeCRUD('hospital/icu', ICUAdmission),

  updateCondition: async (id, condition, notes) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/icu/${id}/condition`, { condition, notes }));
      return { success: true, data: ICUAdmission.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  discharge: async (id, dischargeData) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/icu/${id}/discharge`, dischargeData));
      return { success: true, data: ICUAdmission.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 20. MATERNITY WARD
// ─────────────────────────────────────────────────────────────────────────────
export const maternityController = {
  ...makeCRUD('hospital/maternity', MaternityCase),

  recordDelivery: async (id, deliveryData) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/maternity/${id}/delivery`, deliveryData));
      return { success: true, data: MaternityCase.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 21. BILLING
// ─────────────────────────────────────────────────────────────────────────────
export const billingController = {
  ...makeCRUD('hospital/billing', HospitalBill),

  recordPayment: async (id, { amount, paymentMethod, notes }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/billing/${id}/payment`, { amount, paymentMethod, notes }));
      return { success: true, data: HospitalBill.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  generateFromVisit: async (visitId, visitType) => {
    try {
      const raw = unwrap(await apiService.post(`hospital/billing/generate`, { visitId, visitType }));
      return { success: true, data: HospitalBill.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  applyDiscount: async (id, { discount, reason }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/billing/${id}/discount`, { discount, reason }));
      return { success: true, data: HospitalBill.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  getRevenueSummary: async (companyId, dateRange) => {
    try {
      const qp  = buildQP({ companyId, ...dateRange });
      const raw = unwrap(await apiService.get(`hospital/billing/revenue?${qp}`));
      return { success: true, data: raw.data };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 22. INSURANCE
// ─────────────────────────────────────────────────────────────────────────────
export const insuranceController = {
  ...makeCRUD('hospital/insurance', InsuranceClaim),

  updateStatus: async (id, { status, approvedAmount, rejectedReason }) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/insurance/${id}/status`, { status, approvedAmount, rejectedReason }));
      return { success: true, data: InsuranceClaim.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  uploadDocument: async (id, file) => {
    try {
      const fd = new FormData();
      fd.append('document', file);
      const raw = unwrap(await apiService.post(`hospital/insurance/${id}/documents`, fd));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 23. PRESCRIPTIONS
// ─────────────────────────────────────────────────────────────────────────────
export const prescriptionController = {
  ...makeCRUD('hospital/prescriptions', Prescription),

  markDispensed: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/prescriptions/${id}/dispense`, { dispensed: true, dispensedAt: new Date().toISOString() }));
      return { success: true, data: Prescription.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  getByPatient: async (patientId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/prescriptions?patientId=${patientId}`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => Prescription.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 24. VITAL SIGNS
// ─────────────────────────────────────────────────────────────────────────────
export const vitalController = {
  ...makeCRUD('hospital/vital-signs', VitalSign),

  getByPatient: async (patientId, limit = 10) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/vital-signs?patientId=${patientId}&limit=${limit}&sortBy=recordedAt&sortOrder=desc`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => VitalSign.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },

  getLatest: async (patientId) => {
    try {
      const raw = unwrap(await apiService.get(`hospital/vital-signs/latest/${patientId}`));
      return { success: true, data: VitalSign.fromJSON(raw.data || raw) };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 25. DIET & NUTRITION
// ─────────────────────────────────────────────────────────────────────────────
export const dietController = {
  ...makeCRUD('hospital/diet-nutrition', DietPlan),

  getByPatient: async (patientId) => {
    try {
      const raw  = unwrap(await apiService.get(`hospital/diet-nutrition?patientId=${patientId}&status=Active`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => DietPlan.fromJSON(d)) };
    } catch (err) {
      return { success: false, data: [], message: err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 26. MORTALITY RECORDS
// ─────────────────────────────────────────────────────────────────────────────
export const mortalityController = {
  ...makeCRUD('hospital/mortality', MortalityRecord),

  issueCertificate: async (id) => {
    try {
      const raw = unwrap(await apiService.post(`hospital/mortality/${id}/certificate`, {}));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },

  notifyRelative: async (id, notificationData) => {
    try {
      const raw = unwrap(await apiService.patch(`hospital/mortality/${id}/notify`, notificationData));
      return { success: true, data: MortalityRecord.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD STATS (all modules summary)
// ─────────────────────────────────────────────────────────────────────────────
export const hospitalDashboardController = {
  getSummary: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`hospital/dashboard/summary${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: raw.data };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  },
};

export default {
  patientController, medicalRecordController, appointmentController,
  doctorController, nurseController, departmentController,
  wardController, bedController, opdController, ipdController,
  emergencyController, labController, pathologyController,
  radiologyController, pharmacyController, bloodBankController,
  surgeryController, ambulanceController, icuController,
  maternityController, billingController, insuranceController,
  prescriptionController, vitalController, dietController,
  mortalityController, hospitalDashboardController,
};